import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

export default function AdminRequests() {
  const [requests, setRequests] = useState([]);
  const [hotOnly, setHotOnly] = useState(false);
  const [active, setActive] = useState(null);
  const load = () => api.get("/requests").then((r) => setRequests(r.data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/requests/${id}`, { status });
    load();
    setActive((a) => (a && a.request_id === id ? { ...a, status } : a));
  };

  const isHot = (r) => Boolean(r.notes && r.notes.includes("[ad attribution:"));
  const parseUtm = (notes) => {
    const m = notes?.match(/\[ad attribution:\s*([^\]]+)\]/);
    if (!m) return null;
    const out = {};
    m[1].split(",").forEach((pair) => {
      const [k, v] = pair.split("=").map((s) => s.trim());
      if (k && v) out[k] = v;
    });
    return out;
  };

  const visible = hotOnly ? requests.filter(isHot) : requests;
  const hotCount = requests.filter(isHot).length;

  return (
    <div data-testid="admin-requests">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline">Inbound</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] mt-2">Service Requests</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#475569]" data-testid="hot-count">
            <span className="font-bold text-[#C9A356]">{hotCount}</span> attributed lead{hotCount === 1 ? "" : "s"}
          </span>
          <button
            onClick={() => setHotOnly(!hotOnly)}
            data-testid="hot-leads-toggle"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${
              hotOnly
                ? "bg-[#C9A356] text-white"
                : "bg-white border-2 border-[#E2E8F0] text-[#0A2540] hover:border-[#C9A356]"
            }`}
          >
            <span>🔥</span> {hotOnly ? "Showing hot only" : "Hot leads"}
          </button>
        </div>
      </div>

      <div className="card-pb overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-[#E2E8F0]">
            <tr>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-[#475569]">Customer</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-[#475569]">Type</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-[#475569]">Notes / Source</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-[#475569]">Status</th>
              <th className="text-right px-5 py-3 text-xs font-bold uppercase text-[#475569]">Date</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => {
              const hot = isHot(r);
              const utm = hot ? parseUtm(r.notes) : null;
              const cleanNote = (r.notes || "").replace(/\[ad attribution:[^\]]+\]/, "").trim();
              return (
                <tr
                  key={r.request_id}
                  onClick={() => setActive(r)}
                  className={`border-b border-[#E2E8F0] cursor-pointer ${hot ? "bg-[#FFFBF0] hover:bg-[#FFF6E0]" : "hover:bg-slate-50"}`}
                  data-testid={`request-row-${r.request_id}`}
                >
                  <td className="px-5 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      {hot && <span className="text-base" title="Hot lead from ad" data-testid={`hot-badge-${r.request_id}`}>🔥</span>}
                      <div>
                        <div className="font-medium text-[#0A2540] dark:text-white" data-testid={`client-name-${r.request_id}`}>
                          {r.client_name || "—"}
                        </div>
                        <div className="text-xs text-[#475569] dark:text-slate-400 font-mono">{r.customer_code}</div>
                        {r.client_email && (
                          <div className="text-[10px] text-[#94A3B8] truncate max-w-[180px]">{r.client_email}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm capitalize">{r.request_type}</td>
                  <td className="px-5 py-3 text-sm text-[#475569] max-w-md">
                    <div className="truncate">{cleanNote || "—"}</div>
                    {r.appointment_at && (
                      <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#0A2540] text-[#C9A356]" data-testid={`appointment-${r.request_id}`}>
                        📅 {new Date(r.appointment_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </div>
                    )}
                    {utm && (
                      <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`utm-tags-${r.request_id}`}>
                        {Object.entries(utm).map(([k, v]) => (
                          <span
                            key={k}
                            className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#C9A356]/15 text-[#0A2540] border border-[#C9A356]/30"
                          >
                            {k.replace("utm_", "")}:{v}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={r.status}
                      onChange={(e) => updateStatus(r.request_id, e.target.value)}
                      data-testid={`request-status-${r.request_id}`}
                      className="text-xs border border-[#E2E8F0] rounded px-2 py-1 bg-white"
                    >
                      <option value="new">new</option>
                      <option value="reviewed">reviewed</option>
                      <option value="scheduled">scheduled</option>
                      <option value="converted">converted</option>
                      <option value="closed">closed</option>
                    </select>
                  </td>
                  <td className="px-5 py-3 text-right text-xs text-[#475569]">
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr><td colSpan="5" className="px-5 py-8 text-center text-[#475569]">
                {hotOnly ? "No attributed leads yet — share your calculator with UTM-tagged links!" : "No requests yet"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {active && <RequestDetailModal req={active} onClose={() => setActive(null)} onUpdate={updateStatus} />}
    </div>
  );
}

function RequestDetailModal({ req, onClose, onUpdate }) {
  const cleanNote = (req.notes || "").replace(/\[ad attribution:[^\]]+\]/, "").trim();
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="request-detail-modal">
        <div className="px-6 py-4 border-b border-[#E2E8F0] dark:border-slate-700 flex items-start justify-between">
          <div>
            <p className="overline">Service request · {req.request_id}</p>
            <h3 className="font-heading text-2xl text-[#0A2540] dark:text-white capitalize mt-1">{req.request_type?.replace(/_/g, " ")}</h3>
          </div>
          <button onClick={onClose} className="text-[#475569] hover:text-[#0A2540] dark:hover:text-white" aria-label="Close">✕</button>
        </div>
        <div className="p-6 space-y-5">
          <div className="border border-[#E2E8F0] dark:border-slate-700 rounded-md p-4">
            <div className="overline mb-2">Customer</div>
            <div className="font-heading text-lg text-[#0A2540] dark:text-white">{req.client_name || "—"}</div>
            <div className="text-xs font-mono text-[#C9A356] mt-0.5">{req.customer_code || ""}</div>
            <div className="mt-3 grid sm:grid-cols-2 gap-y-1 text-sm">
              {req.client_email && (
                <a href={`mailto:${req.client_email}`} className="text-[#475569] dark:text-slate-300 hover:text-[#C9A356] inline-flex items-center gap-1.5" data-testid="req-email">
                  ✉ {req.client_email}
                </a>
              )}
              {req.client_phone && (
                <a href={`tel:${req.client_phone}`} className="text-[#475569] dark:text-slate-300 hover:text-[#C9A356] inline-flex items-center gap-1.5" data-testid="req-phone">
                  ☎ {req.client_phone}
                </a>
              )}
            </div>
            {req.user_id && (
              <Link to={`/admin/clients/${req.user_id}`} className="text-xs text-[#C9A356] hover:underline mt-3 inline-block" data-testid="open-client-from-req">
                Open full client profile →
              </Link>
            )}
          </div>

          {cleanNote && (
            <div>
              <div className="overline mb-1">Notes</div>
              <p className="text-sm text-[#0A2540] dark:text-white whitespace-pre-wrap">{cleanNote}</p>
            </div>
          )}

          {req.appointment_at && (
            <div className="bg-[#0A2540] text-[#C9A356] rounded-md p-3 text-sm">
              📅 Appointment: {new Date(req.appointment_at).toLocaleString()}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-xs">
            <Detail label="Status">
              <select value={req.status} onChange={(e) => onUpdate(req.request_id, e.target.value)} data-testid="req-status-select"
                      className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded px-2 py-1.5">
                <option value="new">new</option>
                <option value="reviewed">reviewed</option>
                <option value="scheduled">scheduled</option>
                <option value="converted">converted</option>
                <option value="closed">closed</option>
              </select>
            </Detail>
            <Detail label="Submitted">
              <span className="text-[#475569] dark:text-slate-300">{new Date(req.created_at).toLocaleString()}</span>
            </Detail>
            {req.property_type && <Detail label="Property"><span className="text-[#475569] dark:text-slate-300 capitalize">{req.property_type}</span></Detail>}
            {req.roof_type && <Detail label="Roof"><span className="text-[#475569] dark:text-slate-300 capitalize">{req.roof_type}</span></Detail>}
            {req.monthly_bill && <Detail label="Monthly bill"><span className="text-[#475569] dark:text-slate-300">${req.monthly_bill}</span></Detail>}
            {req.battery_backup && <Detail label="Battery"><span className="text-[#475569] dark:text-slate-300">{req.battery_backup ? "Yes" : "No"}</span></Detail>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8] mb-1">{label}</div>
      {children}
    </div>
  );
}

